import React, { useCallback, useContext } from 'react';
import { AppContext } from '@prisjakt/ui-app-context';

import { ParamsAndReturnTypes } from '@prisjakt/ui-translate/src/translate-func';

import GoToStoreButton from '../go-to-store-button';
import PriceColumn from '../price-column';
import CampaignBadge from '../campaign-badge';

import { AlternativePrice, Price } from '../../../../types';
import { TableLinkProps } from '../../types';
import { SortOption } from '../../../../areas/product/components/sort-options/types';

import * as Table from '../../styles/table';
import * as S from './styles';

type OwnProps = {
  campaignStyling?: { background: string; text: string; name: string } | null;
  externalUri?: string | null;
  onClickOut: (e: React.MouseEvent, id?: string | null) => void;
  isInternationalPrice: boolean;
  price: AlternativePrice;
  priceStore?: Price['store'];
  tableLinkProps: TableLinkProps;
  currentSort: SortOption;
};

const AlternativePriceRow = ({
  campaignStyling,
  externalUri,
  onClickOut,
  isInternationalPrice,
  price,
  priceStore,
  tableLinkProps,
  currentSort,
}: OwnProps) => {
  const { t } = useContext(AppContext);

  const featured = priceStore && priceStore.featured;

  const handleClickOut = useCallback(
    (e: React.MouseEvent) => onClickOut(e, price.id),
    [price.id, onClickOut]
  );

  const renderProductNameColumn = () => (
    <Table.Column colSpan={3} expandOnMobile>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        vertical
        alignLeft
      >
        <div>
          <S.ProductName>{price.name}</S.ProductName>
          <S.StockStatus>
            {price && price.stock && price.stock.statusText
              ? price.stock.statusText.replace(/-/g, '\u2011')
              : t(
                  'product',
                  `stock-status.${(price.stock && price.stock.status) ||
                    'unknown'}` as keyof ParamsAndReturnTypes['product']
                )}
          </S.StockStatus>
        </div>
      </Table.Link>
    </Table.Column>
  );

  const renderStockStatusColumn = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignCenter
      >
        <S.StockStatusColumn
          status={
            price.stock && price.stock.status ? price.stock.status : undefined
          }
          statusText={
            price.stock && price.stock.statusText
              ? price.stock.statusText
              : undefined
          }
          countryCode={priceStore && priceStore.countryCode}
          toneDown={!featured}
          isInternationalPrice={isInternationalPrice}
        />
      </Table.Link>
    </Table.Column>
  );

  const renderPriceColumn = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignRight
      >
        {price.price && (
          <div>
            <CampaignBadge campaignStyling={campaignStyling} />
            <PriceColumn
              price={price.price}
              storeIsFeatured={featured}
              campaignStyling={campaignStyling}
              align="right"
              currentSort={currentSort}
            />
          </div>
        )}
      </Table.Link>
    </Table.Column>
  );

  const renderGoToStoreButton = () => (
    <Table.Column>
      <Table.Link
        {...tableLinkProps}
        onClick={handleClickOut}
        href={price.externalUri!}
        alignRight
      >
        <GoToStoreButton
          visible={Boolean(externalUri || featured)}
          disabled={Boolean(externalUri && !featured)}
          featured={Boolean(featured)}
        />
      </Table.Link>
    </Table.Column>
  );

  return (
    <Table.AltRow>
      {renderProductNameColumn()}
      {renderStockStatusColumn()}
      {renderPriceColumn()}
      {renderGoToStoreButton()}
    </Table.AltRow>
  );
};

export default AlternativePriceRow;
